import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import LoadingSpinner from '../common/LoadingSpinner';
import { notificationService } from '../../services/notificationService';

const NotificationPreferences = () => {
  const { isAuthenticated } = useAuth();
  const { showSuccess, showError } = useToast();
  
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load preferences
  useEffect(() => {
    if (isAuthenticated) {
      loadPreferences();
    }
  }, [isAuthenticated]);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const response = await notificationService.getPreferences();
      setPreferences(response);
    } catch (error) {
      console.error('Error loading preferences:', error);
      showError('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const handlePreferenceChange = (category, key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    if (!preferences) return;

    setSaving(true);
    try {
      await notificationService.updatePreferences(preferences);
      showSuccess('Notification preferences saved successfully');
    } catch (error) {
      console.error('Error saving preferences:', error);
      showError('Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (preferences) {
      loadPreferences();
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Please log in to manage notification preferences.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="medium" />
      </div>
    );
  }

  if (!preferences) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Failed to load notification preferences.</p>
        <button
          onClick={loadPreferences}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  const notificationTypes = [
    { key: 'message', label: 'Messages', description: 'Notifications for new messages' },
    { key: 'ad_message', label: 'Ad Messages', description: 'Notifications for ad-related messages' },
    { key: 'follow', label: 'Follows', description: 'Notifications when someone follows your content' },
    { key: 'like', label: 'Likes', description: 'Notifications when someone likes your content' },
    { key: 'comment', label: 'Comments', description: 'Notifications for new comments' },
    { key: 'review', label: 'Reviews', description: 'Notifications for new reviews' },
    { key: 'announcement', label: 'Announcements', description: 'System announcements and updates' },
    { key: 'system', label: 'System', description: 'System notifications and alerts' },
    { key: 'reminder', label: 'Reminders', description: 'Reminder notifications' },
    { key: 'update', label: 'Updates', description: 'Content update notifications' }
  ];

  const channels = [
    { key: 'email', label: 'Email', description: 'Receive notifications via email' },
    { key: 'push', label: 'Push', description: 'Receive push notifications' },
    { key: 'in_app', label: 'In-App', description: 'Show notifications in the app' }
  ];

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Notification Preferences</h1>
        <p className="text-gray-600 mt-2">Manage how you receive notifications</p>
      </div>

      <div className="space-y-8">
        {/* Global Settings */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Global Settings</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {channels.map(channel => (
              <div key={channel.key} className="flex items-center">
                <input
                  type="checkbox"
                  id={`${channel.key}_enabled`}
                  checked={preferences[`${channel.key}_enabled`] || false}
                  onChange={(e) => handlePreferenceChange('global', `${channel.key}_enabled`, e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor={`${channel.key}_enabled`} className="ml-3">
                  <div className="text-sm font-medium text-gray-900">{channel.label}</div>
                  <div className="text-sm text-gray-500">{channel.description}</div>
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Notification Types */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Notification Types</h2>
          
          <div className="space-y-6">
            {channels.map(channel => (
              <div key={channel.key}>
                <h3 className="text-md font-medium text-gray-900 mb-3">{channel.label} Notifications</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {notificationTypes.map(type => (
                    <div key={`${channel.key}_${type.key}`} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`${channel.key}_${type.key}`}
                        checked={preferences[`${channel.key}_${type.key}`] || false}
                        onChange={(e) => handlePreferenceChange('types', `${channel.key}_${type.key}`, e.target.checked)}
                        disabled={!preferences[`${channel.key}_enabled`]}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                      />
                      <label htmlFor={`${channel.key}_${type.key}`} className="ml-3">
                        <div className="text-sm font-medium text-gray-900">{type.label}</div>
                        <div className="text-sm text-gray-500">{type.description}</div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quiet Hours */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quiet Hours</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="quiet_hours_enabled"
                checked={preferences.quiet_hours_enabled || false}
                onChange={(e) => handlePreferenceChange('quiet', 'quiet_hours_enabled', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="quiet_hours_enabled" className="ml-3">
                <div className="text-sm font-medium text-gray-900">Enable Quiet Hours</div>
                <div className="text-sm text-gray-500">Pause notifications during specified hours</div>
              </label>
            </div>
            
            {preferences.quiet_hours_enabled && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={preferences.quiet_hours_start || ''}
                    onChange={(e) => handlePreferenceChange('quiet', 'quiet_hours_start', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                  <input
                    type="time"
                    value={preferences.quiet_hours_end || ''}
                    onChange={(e) => handlePreferenceChange('quiet', 'quiet_hours_end', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                  <select
                    value={preferences.quiet_hours_timezone || 'UTC'}
                    onChange={(e) => handlePreferenceChange('quiet', 'quiet_hours_timezone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="UTC">UTC</option>
                    <option value="Asia/Karachi">Asia/Karachi</option>
                    <option value="America/New_York">America/New_York</option>
                    <option value="Europe/London">Europe/London</option>
                  </select>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Digest Frequency */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Digest Frequency</h2>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Digest</label>
            <select
              value={preferences.digest_frequency || 'immediate'}
              onChange={(e) => handlePreferenceChange('digest', 'digest_frequency', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="immediate">Immediate</option>
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="never">Never</option>
            </select>
            <p className="text-sm text-gray-500 mt-1">
              How often to receive email digests of notifications
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            onClick={handleReset}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {saving && <LoadingSpinner size="small" className="mr-2" />}
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferences;
